/**
 * The rules both printer selectors share.
 *
 * Listings and Configure → Printers are two views over one source of truth — the local hardware
 * service's saved preference — not two selection systems. Everything that decides what to show or
 * what a state means lives here, so the two screens cannot drift into disagreeing about whether a
 * printer is usable. The components hold layout and nothing else.
 */

import type { DetectedPrinter, LabelPrinterProvider, PrinterSelectionState } from './labelPrinting';

/**
 * What is honestly known about one printer.
 *
 * `unknown` is a real answer, not a fallback: a queue whose transport cannot be probed without
 * doing I/O to the device gives no evidence either way, and reporting that as `ready` is how a
 * service comes to accept a job for a printer sitting in a drawer.
 */
export type PrinterHealth = 'ready' | 'offline' | 'unavailable' | 'error' | 'unknown';

/** Which health a detected printer is in. */
export function describePrinterHealth(printer: DetectedPrinter): PrinterHealth {
    if (printer.ready) {
        // Ready with a reason attached means the platform could not confirm presence. Still
        // printable, but the operator should know the confirmation is missing.
        return printer.statusReason == null || printer.statusReason === '' ? 'ready' : 'unknown';
    }

    if (!printer.installed) {
        return 'unavailable';
    }

    if (!printer.connected) {
        return 'offline';
    }

    const reason = printer.statusReason?.toLowerCase() ?? '';

    return reason.includes('error') || reason.includes('jam') ? 'error' : 'unavailable';
}

/** One row, as both screens render it. */
export interface PrinterRow {
    id: string;
    provider: LabelPrinterProvider;
    displayName: string;
    model: string | null;
    health: PrinterHealth;
    statusReason: string | null;
    /** The printer labels are currently going to. */
    effective: boolean;
    /** The printer this workstation was told to prefer, whether or not it is usable. */
    preferred: boolean;
    /** Whether choosing this one would do anything. */
    selectable: boolean;
}

/** What both screens need in order to render, derived once. */
export interface PrinterSelectionView {
    rows: PrinterRow[];
    effectivePrinterId: string | null;
    preferredPrinterId: string | null;
    /** True when a label can be sent right now. */
    canPrint: boolean;
    /**
     * Name of the printer a failover moved away from, when one just happened.
     *
     * Drives the toast. A failover nobody is told about is how a label reaches a printer loaded
     * with different media without anyone noticing, which is what makes automatic failover
     * acceptable in the first place.
     */
    failedOverFrom: string | null;
    /** Non-sensitive explanation when printing is blocked. */
    blockedReason: string | null;
}

/**
 * Collapse a service response into what a screen renders.
 *
 * Called by both selectors with the same input, so a printer that reads "offline" in Configure
 * cannot read "ready" in Listings.
 */
export function buildPrinterSelectionView(
    state: PrinterSelectionState | null,
): PrinterSelectionView {
    if (state === null) {
        return {
            rows: [],
            effectivePrinterId: null,
            preferredPrinterId: null,
            canPrint: false,
            failedOverFrom: null,
            blockedReason: null,
        };
    }

    const effectivePrinterId = state.selectedPrinterId ?? null;
    const preferredPrinterId = state.preferredPrinterId ?? null;

    const rows = state.printers.map<PrinterRow>(printer => ({
        id: printer.id,
        provider: printer.provider,
        displayName: printer.displayName,
        model: printer.model ?? null,
        health: describePrinterHealth(printer),
        statusReason: printer.statusReason ?? null,
        effective: printer.id === effectivePrinterId,
        preferred: printer.id === preferredPrinterId,
        // Only a printer the service can actually reach may be chosen. Offering an offline one
        // would write a preference that no device satisfies, and the failure would surface later,
        // at a counter, mid-transaction.
        selectable: printer.ready,
    }));

    return {
        rows,
        effectivePrinterId,
        preferredPrinterId,
        canPrint: state.ready,
        failedOverFrom: state.failedOverFrom ?? null,
        blockedReason: state.ready ? null : state.statusReason ?? null,
    };
}

/**
 * Whether a newly arrived service state should replace the one on screen.
 *
 * Multiple tabs, a device notification and a manual refresh can all land at once, and the answer
 * has to be "the latest confirmed service state wins" rather than "whichever response resolved
 * last". `observedAt` is stamped by the service from its own clock, so it orders responses by when
 * the platform was actually read instead of by network timing.
 */
export function isNewerServiceState(
    incoming: PrinterSelectionState,
    current: PrinterSelectionState | null,
): boolean {
    if (current === null) {
        return true;
    }

    const incomingAt = Date.parse(incoming.observedAt);
    const currentAt = Date.parse(current.observedAt);

    if (Number.isNaN(incomingAt) || Number.isNaN(currentAt)) {
        // An unparseable timestamp is not a reason to freeze the screen on stale data.
        return true;
    }

    return incomingAt >= currentAt;
}

/** Service error code meaning the label must be rebuilt for a different printer family. */
export const PROVIDER_CHANGED_CODE = 'PRINTER_PROVIDER_CHANGED';

/** Service error code meaning a job reached the spooler and its outcome is unknown. */
export const PRINT_UNCERTAIN_CODE = 'PRINT_RESULT_UNCERTAIN';

/** What the caller should do about a failed job. */
export type PrintRecoveryAction =
    /** Rebuild the payload for the named provider and try once more. */
    | { action: 'rebuild'; provider: LabelPrinterProvider }
    /** Stop and ask a person: the label may already have printed. */
    | { action: 'acknowledge' }
    /** Nothing automatic is appropriate. */
    | { action: 'report' };

/**
 * Decide what to do after a job failed.
 *
 * The two special cases are opposites, and conflating them is how a counter ends up with two
 * physical labels for one object:
 *
 * - `PRINTER_PROVIDER_CHANGED` is raised **before** submission, so nothing has reached the
 *   spooler and a rebuild-and-retry is safe. The payload must be rebuilt rather than resent,
 *   because a raster or command stream must never cross a provider boundary.
 * - `PRINT_RESULT_UNCERTAIN` is raised **after** the job reached the spooler, so the label may
 *   already be printed. Retrying is exactly the wrong move; a person decides.
 *
 * `alreadyRetried` guards the one-retry rule: a rebuild that fails again is reported, never
 * rebuilt a second time.
 */
export function decidePrintRecovery(
    errorCode: string | undefined,
    renderForProvider: LabelPrinterProvider | undefined,
    alreadyRetried: boolean,
): PrintRecoveryAction {
    if (errorCode === PRINT_UNCERTAIN_CODE) {
        return { action: 'acknowledge' };
    }

    if (errorCode === PROVIDER_CHANGED_CODE && renderForProvider !== undefined && !alreadyRetried) {
        return { action: 'rebuild', provider: renderForProvider };
    }

    return { action: 'report' };
}
